import MuiIconButton, {
  type IconButtonProps as MuiIconButtonProps,
} from '@mui/material/IconButton';
import type { CSSObject } from '@mui/material/styles';
import type { ReactElement } from 'react';

import { cssVar } from '../../theme/tokens';
import { type ControlSize, MUI_SIZE, iconSize, mergeSx } from './sizes';
import { Tooltip } from './Tooltip';

export type IconButtonVariant = 'ghost' | 'outline' | 'primary' | 'secondary' | 'danger';

export interface IconButtonProps extends Omit<
  MuiIconButtonProps,
  'size' | 'color' | 'children' | 'aria-label'
> {
  /** Accessible name, also the tooltip text. Required: an icon alone has no name. */
  label: string;
  /** The icon. */
  children: ReactElement;
  variant?: IconButtonVariant;
  size?: ControlSize;
  /** Show `label` as a tooltip on hover and focus (default true). */
  tooltip?: boolean;
}

const VARIANT_SX: Record<IconButtonVariant, CSSObject> = {
  ghost: { color: 'inherit' },
  outline: {
    color: cssVar('text'),
    border: `1px solid ${cssVar('border-strong')}`,
    '&:hover': { bgcolor: cssVar('hover') },
  },
  primary: {
    color: cssVar('text-on-primary'),
    bgcolor: cssVar('primary'),
    '&:hover': { bgcolor: cssVar('primary-hover') },
  },
  secondary: {
    color: cssVar('text-on-secondary'),
    bgcolor: cssVar('secondary'),
    '&:hover': { bgcolor: cssVar('secondary-hover') },
  },
  danger: {
    color: cssVar('danger-text'),
    '&:hover': { bgcolor: cssVar('danger-subtle') },
  },
};

/**
 * A square button holding one icon, as tall as the other controls of its size. The label is the
 * accessible name and, by default, a tooltip.
 */
export function IconButton({
  label,
  children,
  variant = 'ghost',
  size = 'md',
  tooltip = true,
  sx,
  ...props
}: IconButtonProps) {
  const button = (
    <MuiIconButton
      aria-label={label}
      size={MUI_SIZE[size]}
      sx={mergeSx(
        { ...VARIANT_SX[variant], '& .MuiSvgIcon-root': { fontSize: iconSize(size) } },
        sx,
      )}
      {...props}
    >
      {children}
    </MuiIconButton>
  );
  // A disabled button fires no events, so it gets no tooltip.
  return tooltip && !props.disabled ? <Tooltip title={label}>{button}</Tooltip> : button;
}
